<template>
  <div class="auto-sign-excluded">
    <div class="box-title">
      <h4>
        <i class="fal fa-ban"></i>
        ສາຂາປາຍທາງ ບໍ່ສຳເລັດບິນ Auto
      </h4>
    </div>

    <p class="setting-hint">
      ບິນທີ່ມີ <strong>ສາຂາປາຍທາງ</strong> ຢູ່ໃນລາຍການນີ້ ຈະບໍ່ຖືກ “ສຳເລັດບິນ Auto” ຕອນກາງຄືນ.
      ເພີ່ມສາຂາໄດ້ແບບບໍ່ມີກຳນົດ ຫຼື ໃສ່ວັນທີ — ພໍເກີນວັນທີນັ້ນ ລະບົບຈະສຳເລັດບິນ Auto ໃຫ້ສາຂານັ້ນຄືນປົກກະຕິເອງ
      ບໍ່ຕ້ອງມາລຶບອອກ.
    </p>

    <div v-if="loadError" class="notification is-warning setting-alert">
      <i class="fal fa-exclamation-triangle"></i>
      {{ loadErrorText }}
      <vs-button transparent class="setting-alert-action" @click="fetchData()">ລອງໃໝ່</vs-button>
    </div>

    <!--
      is_persisted=false ໝາຍຄວາມວ່າ ຕາຕະລາງຝັ່ງ backend ຍັງບໍ່ໄດ້ migrate.
      ລາຍການທີ່ເຫັນແມ່ນຄ່າມາດຕະຖານໃນ code (ຍັງໃຊ້ງານປົກກະຕິ) ແຕ່ເພີ່ມ/ລຶບຍັງບໍ່ໄດ້.
    -->
    <div v-else-if="!isPersisted" class="notification is-warning setting-alert">
      <i class="fal fa-database"></i>
      ຕາຕະລາງການຕັ້ງຄ່າຍັງບໍ່ໄດ້ຕິດຕັ້ງ (migration ຍັງບໍ່ໄດ້ດຳເນີນ) —
      ລາຍການລຸ່ມນີ້ແມ່ນ <strong>ຄ່າມາດຕະຖານໃນລະບົບ</strong> ເຊິ່ງໃຊ້ງານປົກກະຕິຢູ່ ແຕ່ຍັງເພີ່ມ/ລຶບບໍ່ໄດ້.
    </div>

    <!--
      ອ່ານຕາຕະລາງບໍ່ໄດ້ ≠ ຍັງບໍ່ໄດ້ migrate: ຕອນນີ້ຮອບກາງຄືນກຳລັງໃຊ້ລາຍການສຳຮອງໃນ code
      ບໍ່ແມ່ນລາຍການໃນຕາຕະລາງ — ຈຶ່ງລັອກການເພີ່ມ/ແກ້ໄຂໄວ້ ບໍ່ໃຫ້ບັນທຶກລົງບ່ອນທີ່ຍັງເບິ່ງບໍ່ເຫັນ.
    -->
    <div v-else-if="isFallback" class="notification is-danger setting-alert">
      <i class="fal fa-exclamation-triangle"></i>
      ອ່ານລາຍການຈາກຖານຂໍ້ມູນ<strong>ບໍ່ໄດ້</strong> — ຮອບກາງຄືນຈະໃຊ້ລາຍການສຳຮອງໃນລະບົບແທນ
      ຈົນກວ່າຈະອ່ານໄດ້ຄືນ. ການເພີ່ມ/ແກ້ໄຂຖືກປິດໄວ້ຊົ່ວຄາວ. ກະລຸນາແຈ້ງທີມ IT.
    </div>

    <CacheStaleNotice :visible="cacheStale">ຮອບກາງຄືນອາດຍັງໃຊ້ລາຍການເກົ່າ</CacheStaleNotice>

    <div class="setting-summary" v-if="!loadError">
      <div class="setting-summary-item">
        <span class="setting-summary-label">ກຳລັງຍົກເວັ້ນຢູ່ຕອນນີ້</span>
        <span class="setting-summary-value">{{ activeRows.length }} ສາຂາ</span>
      </div>
      <div class="setting-summary-item">
        <span class="setting-summary-label">ມີກຳນົດວັນ</span>
        <span class="setting-summary-value">{{ datedActiveCount }} ສາຂາ</span>
      </div>
      <div class="setting-summary-item">
        <span class="setting-summary-label">ໝົດກຳນົດແລ້ວ</span>
        <span class="setting-summary-value">{{ expiredRows.length }} ສາຂາ</span>
      </div>
      <div class="setting-summary-item">
        <span class="setting-summary-label">ມື້ນີ້ (ຕາມເຄື່ອງແມ່ຂ່າຍ)</span>
        <span class="setting-summary-value">{{ formatDate(today) }}</span>
      </div>
    </div>

    <!-- ເພີ່ມສາຂາ -->
    <div class="setting-group">
      <div class="setting-group-head">
        <h5>ເພີ່ມສາຂາປາຍທາງ</h5>
      </div>

      <div class="columns is-multiline">
        <div class="column is-5">
          <label class="label">
            ສາຂາ
            <span class="text-danger">{{ addErrors.branch_id || '' }}</span>
          </label>
          <multiselect
              v-model="form.branch"
              :options="branchOptions"
              :loading="searchingBranches"
              :internal-search="false"
              :clear-on-select="false"
              :preserve-search="true"
              :show-no-results="true"
              :disabled="!canWrite"
              label="name"
              track-by="id"
              placeholder="ພິມຊື່ ຫຼື ລະຫັດສາຂາ..."
              select-label=""
              selected-label=""
              deselect-label="ເອົາອອກ"
              @search-change="searchBranches"
              @open="searchBranches(searchTerm)"
          >
            <template #option="{ option }">
              <span class="option-name">{{ option.name }}</span>
              <small class="option-meta">
                #{{ option.id }}
                <span v-if="option.code" class="option-code"> · {{ option.code }}</span>
                <span v-if="option.province_prefix || option.province_name">
                  · {{ [option.province_prefix, option.province_name].filter(Boolean).join(' ') }}
                </span>
                <span v-if="isClosed(option.status)" class="option-closed"> · ປິດຢູ່</span>
                <span v-if="activeIdSet.has(Number(option.id))" class="option-listed"> · ຢູ່ໃນລາຍການແລ້ວ</span>
              </small>
            </template>
            <template #noResult>ບໍ່ພົບສາຂາ</template>
            <template #noOptions>ພິມເພື່ອຄົ້ນຫາສາຂາ</template>
          </multiselect>
          <!--
            ສາຂາທີ່ຢູ່ໃນລາຍການແລ້ວ ໃຫ້ໄປແກ້ໄຂແຖວຂອງມັນ — ບໍ່ໃຫ້ “ເພີ່ມ” ທັບ ເພາະຈະປ່ຽນ
            ຍົກເວັ້ນແບບບໍ່ມີກຳນົດ ໃຫ້ກາຍເປັນມີກຳນົດ ໂດຍບໍ່ຮູ້ຕົວ. ຝັ່ງ server ກໍປະຕິເສດເຊັ່ນກັນ.
          -->
          <p v-if="selectedIsListed" class="setting-field-note text-warning">
            ສາຂານີ້ຢູ່ໃນລາຍການແລ້ວ — ກະລຸນາແກ້ໄຂແຖວຂອງມັນຢູ່ຕາຕະລາງລຸ່ມນີ້ແທນ.
          </p>
        </div>

        <div class="column is-3">
          <label class="label">
            ຍົກເວັ້ນຮອດວັນທີ (ຮວມມື້ນັ້ນ)
            <span class="text-danger">{{ addErrors.excluded_until || '' }}</span>
          </label>
          <input
              class="input"
              type="date"
              :min="today"
              :disabled="!canWrite"
              v-model="form.excluded_until"
          />
          <p class="setting-field-note">
            <template v-if="form.excluded_until">
              ຈະສຳເລັດບິນ Auto ຄືນ ຕັ້ງແຕ່ {{ formatDate(nextDay(form.excluded_until)) }}
            </template>
            <template v-else>ປະຫວ່າງ = ຍົກເວັ້ນແບບບໍ່ມີກຳນົດ</template>
          </p>
        </div>

        <div class="column is-4">
          <label class="label">
            ເຫດຜົນ (ບໍ່ບັງຄັບ)
            <span class="text-danger">{{ addErrors.reason || '' }}</span>
          </label>
          <input
              class="input"
              type="text"
              maxlength="255"
              placeholder="ເຊັ່ນ: ສາຂາປິດປັບປຸງ ຮອດ 15/09"
              :disabled="!canWrite"
              v-model="form.reason"
          />
        </div>
      </div>

      <div class="setting-actions">
        <vs-button :loading="adding" :disabled="!canAdd" @click="add()">
          <i class="fal fa-plus-circle"></i>
          ເພີ່ມສາຂາ
        </vs-button>
        <vs-button dark transparent :disabled="adding" @click="resetForm()">ລ້າງຟອມ</vs-button>
      </div>
    </div>

    <!--
      ໜຶ່ງຕາຕະລາງ: ແຖວທີ່ກຳລັງຍົກເວັ້ນກ່ອນ ແລ້ວຈຶ່ງແຖວທີ່ໝົດກຳນົດ (ມີແຖວຂັ້ນບອກ).
      ແຖວທີ່ໝົດກຳນົດ ສຳເລັດບິນ Auto ຄືນປົກກະຕິແລ້ວ — ເກັບໄວ້ໃຫ້ເຫັນ ເພື່ອຕໍ່ວັນທີ ຫຼື ລຶບປະຫວັດ.
    -->
    <div class="setting-group">
      <div class="setting-group-head">
        <h5>ລາຍການສາຂາ ({{ activeRows.length }} ກຳລັງຍົກເວັ້ນ)</h5>
      </div>

      <div class="table-wrap">
        <table class="table is-fullwidth is-hoverable excluded-table">
          <thead>
          <tr>
            <th>ສາຂາ</th>
            <th>ຍົກເວັ້ນຮອດວັນທີ</th>
            <th>ເຫດຜົນ</th>
            <th>ຜູ້ເພີ່ມ / ແກ້ໄຂ</th>
            <th class="col-actions"></th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="!orderedRows.length">
            <td colspan="5" class="has-text-centered has-text-grey">ບໍ່ມີສາຂາໃນລາຍການ</td>
          </tr>

          <template v-for="row in orderedRows">
            <tr v-if="row.is_expired && row.branch_id === firstExpiredId" :key="'divider-' + row.branch_id" class="section-row">
              <td colspan="5">
                <i class="fal fa-history"></i>
                ໝົດກຳນົດແລ້ວ ({{ expiredRows.length }}) — ສຳເລັດບິນ Auto ຄືນປົກກະຕິແລ້ວ
              </td>
            </tr>

            <tr :key="row.branch_id" :class="{ 'is-locked': row.is_locked, 'is-expired': row.is_expired }">
              <td>
                <div class="branch-cell">
                  <span class="branch-name">{{ row.branch_name || '—' }}</span>
                  <small class="branch-meta">
                    #{{ row.branch_id }}
                    <span v-if="row.branch_code" class="branch-code"> · {{ row.branch_code }}</span>
                    <span v-if="row.branch_type"> · {{ row.branch_type }}</span>
                    <span v-if="isClosed(row.branch_status)" class="text-danger"> · ປິດຢູ່</span>
                  </small>
                  <small class="branch-meta" v-if="row.province_prefix || row.province_name">
                    <i class="fal fa-map-marker-alt"></i>
                    <span v-if="row.province_prefix" class="branch-code">{{ row.province_prefix }}</span>
                    {{ row.province_name || '' }}
                  </small>
                  <small v-if="row.is_locked" class="branch-locked">
                    <i class="fal fa-lock"></i> ຕາມນະໂຍບາຍ — ລຶບ/ໃສ່ວັນທີບໍ່ໄດ້
                  </small>
                </div>
              </td>

              <td>
                <template v-if="modeFor(row) === 'edit' && !row.is_locked">
                  <input class="input is-small" type="date" :min="today" v-model="editing.excluded_until"/>
                  <p class="setting-field-note">
                    <template v-if="editing.excluded_until">ຄືນປົກກະຕິ {{ formatDate(nextDay(editing.excluded_until)) }}</template>
                    <template v-else>ບໍ່ມີກຳນົດ</template>
                  </p>
                  <span class="text-danger">{{ editErrors.excluded_until || '' }}</span>
                </template>
                <template v-else>
                  <span v-if="row.excluded_until">
                    {{ formatDate(row.excluded_until) }}
                    <small class="branch-meta">
                      · {{ row.is_expired ? 'ໝົດກຳນົດ' : 'ຄືນປົກກະຕິ' }} {{ formatDate(row.resumes_on) }}
                    </small>
                  </span>
                  <span v-else class="tag is-light">ບໍ່ມີກຳນົດ</span>
                </template>
              </td>

              <td>
                <template v-if="modeFor(row) === 'edit'">
                  <input class="input is-small" type="text" maxlength="255" v-model="editing.reason"/>
                  <span class="text-danger">{{ editErrors.reason || '' }}</span>
                </template>
                <template v-else>
                  <span :class="{ 'has-text-grey': !row.reason }">{{ row.reason || '—' }}</span>
                </template>
              </td>

              <td>
                <small class="branch-meta" v-if="row.source === 'table'">
                  {{ row.created_by || '—' }}<span v-if="row.created_at"> · {{ formatDateTime(row.created_at) }}</span>
                  <template v-if="row.updated_by && row.updated_at !== row.created_at">
                    <br/>ແກ້ໄຂ: {{ row.updated_by }} · {{ formatDateTime(row.updated_at) }}
                  </template>
                </small>
                <small class="branch-meta" v-else>ຄ່າໃນລະບົບ</small>
              </td>

              <td class="col-actions">
                <template v-if="modeFor(row) === 'edit'">
                  <vs-button size="small" :loading="savingEdit" @click="saveEdit(row)">ບັນທຶກ</vs-button>
                  <vs-button size="small" dark transparent :disabled="savingEdit" @click="clearRowMode()">ຍົກເລີກ</vs-button>
                </template>
                <template v-else-if="modeFor(row) === 'remove'">
                  <span class="confirm-text">{{ row.is_expired ? 'ລຶບປະຫວັດນີ້ອອກ?' : `ລຶບ ${row.branch_name || row.branch_id} ອອກ?` }}</span>
                  <vs-button size="small" danger :loading="removing" @click="remove(row)">ຢືນຢັນ</vs-button>
                  <vs-button size="small" dark transparent :disabled="removing" @click="clearRowMode()">ຍົກເລີກ</vs-button>
                </template>
                <template v-else>
                  <vs-button
                      size="small"
                      transparent
                      :disabled="!canWrite || row.source !== 'table' || busy"
                      @click="startEdit(row)"
                  >
                    <i :class="row.is_expired ? 'fal fa-calendar-plus' : 'fal fa-pencil'"></i>
                    {{ row.is_expired ? 'ຕໍ່ວັນທີ' : 'ແກ້ໄຂ' }}
                  </vs-button>
                  <vs-button
                      size="small"
                      danger
                      transparent
                      :disabled="!canWrite || row.is_locked || row.source !== 'table' || busy"
                      @click="startRemove(row)"
                  >
                    <i class="fal fa-trash-alt"></i> ລຶບ
                  </vs-button>
                </template>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
    </div>

    <p class="setting-footnote">
      ໝາຍເຫດ: ລາຍການນີ້ມີຜົນສະເພາະ “ສຳເລັດບິນ Auto” ຕອນກາງຄືນ — ການສຳເລັດບິນດ້ວຍມືຂອງພະນັກງານບໍ່ກ່ຽວ.
      ໜ້ານີ້ຄຸມສະເພາະ<strong>ສາຂາປາຍທາງ</strong>; ສາຂາ<strong>ຕົ້ນທາງ</strong>ຕ່າງປະເທດ (ໄທ, ຈີນ, ຫວຽດ, Shopee)
      ຖືກຍົກເວັ້ນຕາມລາຍການໃນລະບົບຢູ່ແລ້ວ ບໍ່ຕ້ອງເພີ່ມຢູ່ບ່ອນນີ້.
      ຫຼັງແກ້ໄຂ ລະບົບຈະຮັບຄ່າໃໝ່ພາຍໃນບໍ່ເກີນ 5 ນາທີ.
    </p>

    <Loading v-if="isLoading"/>
  </div>
</template>

<script>
import moment from 'moment';
import Loading from '@/views/Loading';
import CacheStaleNotice from '@/components/CacheStaleNotice';

/**
 * Operator-facing text for every error CODE the backend can return. The backend sends its own
 * Lao `message` too; this copy exists so a code can never reach the screen as a bare token.
 */
const ERROR_CODE_TEXT = {
  TABLE_MISSING: 'ຖານຂໍ້ມູນຝັ່ງ backend ຍັງບໍ່ພ້ອມ (migration ຍັງບໍ່ໄດ້ດຳເນີນ) — ກະລຸນາແຈ້ງທີມ IT',
  BRANCH_NOT_FOUND: 'ບໍ່ພົບສາຂານີ້ໃນລະບົບ — ກະລຸນາເລືອກສາຂາຄືນ',
  NOT_FOUND: 'ສາຂານີ້ບໍ່ໄດ້ຢູ່ໃນລາຍການແລ້ວ (ອາດຖືກລຶບໂດຍຜູ້ອື່ນ) — ກະລຸນາໂຫຼດໜ້າຄືນ',
  LOCKED_BY_POLICY: 'ສາຂານີ້ຖືກຍົກເວັ້ນຕາມນະໂຍບາຍ (ສາຂາຕ່າງປະເທດ / Shopee) — ລຶບ ຫຼື ໃສ່ວັນທີບໍ່ໄດ້',
  ALREADY_LISTED: 'ສາຂານີ້ຢູ່ໃນລາຍການແລ້ວ — ກະລຸນາແກ້ໄຂແຖວຂອງມັນແທນການເພີ່ມໃໝ່',
  INVALID_DATE: 'ຮູບແບບວັນທີບໍ່ຖືກຕ້ອງ — ກະລຸນາເລືອກວັນທີຄືນ',
  DATE_IN_PAST: 'ວັນທີຕ້ອງເປັນມື້ນີ້ ຫຼື ຫຼັງຈາກນີ້',
  WRITE_FAILED: 'ບັນທຶກລົງຖານຂໍ້ມູນບໍ່ສຳເລັດ — ກະລຸນາລອງໃໝ່ອີກຄັ້ງ',
};

/** Sentences for a failure the backend did not describe (or described in English). */
const STATUS_TEXT = {
  401: 'ໝົດເວລາເຂົ້າສູ່ລະບົບ — ກະລຸນາເຂົ້າສູ່ລະບົບຄືນ',
  403: 'ບັນຊີນີ້ບໍ່ມີສິດເບິ່ງ/ແກ້ໄຂການຕັ້ງຄ່ານີ້',
  404: 'ບໍ່ພົບຂໍ້ມູນທີ່ຮ້ອງຂໍ — ກະລຸນາໂຫຼດໜ້າຄືນ',
  409: 'ຂໍ້ມູນຊໍ້າກັບທີ່ມີຢູ່ແລ້ວ — ກະລຸນາໂຫຼດໜ້າຄືນ ແລ້ວກວດລາຍການ',
  419: 'ໝົດເວລາໜ້ານີ້ — ກະລຸນາໂຫຼດໜ້າຄືນ',
  422: 'ຄ່າທີ່ໃສ່ບໍ່ຖືກຕ້ອງ — ກະລຸນາກວດຄືນ',
  429: 'ສົ່ງຄຳຂໍຖີ່ເກີນໄປ — ກະລຸນາລໍຖ້າສັກຄູ່ ແລ້ວລອງໃໝ່',
  500: 'ລະບົບຫຼັງບ້ານເກີດຂໍ້ຜິດພາດ — ກະລຸນາລອງໃໝ່ ຫຼື ແຈ້ງທີມ IT',
  502: 'ລະບົບຫຼັງບ້ານບໍ່ຕອບສະໜອງ — ກະລຸນາລອງໃໝ່ໃນອີກສັກຄູ່',
  503: 'ລະບົບຫຼັງບ້ານຍັງບໍ່ພ້ອມໃຫ້ບໍລິການ — ກະລຸນາລອງໃໝ່ໃນອີກສັກຄູ່',
  504: 'ລະບົບຫຼັງບ້ານຕອບຊ້າເກີນໄປ — ກະລຸນາລອງໃໝ່',
};

/** Laravel's stock English messages — never shown to an operator. */
const LARAVEL_DEFAULT_TEXT = /^(The given data was invalid|Server Error|Unauthenticated|Unauthorized|This action is unauthorized|Forbidden|Not Found|Too Many Attempts|User does not have)/i;

/**
 * ສາຂາປາຍທາງ ບໍ່ສຳເລັດບິນ Auto — api/admin/auto-sign-excluded-branches.
 *
 * The rows here ARE the list the nightly sweep skips (the backend reads the same table through
 * the same service), so what the page shows and what the sweep does cannot drift apart. A row
 * with a date expires by itself; the page only has to show that it did.
 */
export default {
  name: 'AutoSignExcludedBranches',

  components: { Loading, CacheStaleNotice },

  data: () => ({
    isLoading: false,
    loadError: false,
    loadErrorText: '',
    isPersisted: false,
    /// True while the backend is bound to its in-code fallback list (table missing OR unreadable).
    isFallback: false,
    cacheStale: false,
    /// Server date (Asia/Vientiane) — never the browser clock, which can sit behind the server
    /// and let the operator pick a date the backend then rejects. '—' until the first load.
    today: '',
    rows: [],

    form: { branch: null, excluded_until: '', reason: '' },
    addErrors: {},
    adding: false,

    branchOptions: [],
    searchingBranches: false,
    searchTerm: '',
    lastFetchedTerm: null,
    searchTimer: null,
    searchSeq: 0,

    /// One row at a time is in ONE mode — 'edit' or 'remove'. A single slot (not two) so the
    /// two inline UIs can never be open on different rows at once.
    rowMode: { branch_id: null, mode: null },
    editing: { excluded_until: '', reason: '' },
    editErrors: {},
    savingEdit: false,
    removing: false,
  }),

  created() {
    this.fetchData();
  },

  /**
   * The SettingsPage menu destroys this pane on a tab switch. Kill the debounce timer and bump
   * the sequence so an in-flight search response drops itself instead of writing to a dead vm.
   */
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.searchSeq += 1;
  },

  computed: {
    activeRows() {
      return this.rows.filter(row => !row.is_expired);
    },

    expiredRows() {
      return this.rows.filter(row => row.is_expired);
    },

    /// Active first, then expired — the template inserts a divider before the first expired row.
    orderedRows() {
      return this.activeRows.concat(this.expiredRows);
    },

    firstExpiredId() {
      return this.expiredRows.length ? this.expiredRows[0].branch_id : null;
    },

    activeIdSet() {
      return new Set(this.activeRows.map(row => Number(row.branch_id)));
    },

    datedActiveCount() {
      return this.activeRows.filter(row => !!row.excluded_until).length;
    },

    busy() {
      return this.adding || this.savingEdit || this.removing;
    },

    /// Writes need a table that exists AND is being read: an add during a read outage would
    /// succeed server-side and then "disappear" behind the fallback list.
    canWrite() {
      return this.isPersisted && !this.isFallback && !this.loadError;
    },

    selectedIsListed() {
      return !!(this.form.branch && this.activeIdSet.has(Number(this.form.branch.id)));
    },

    canAdd() {
      return this.canWrite && !this.busy && !!(this.form.branch && this.form.branch.id) && !this.selectedIsListed;
    },
  },

  methods: {
    modeFor(row) {
      return Number(this.rowMode.branch_id) === Number(row.branch_id) ? this.rowMode.mode : null;
    },

    isClosed(status) {
      // `!= null` on purpose: an absent field must not read as "closed".
      return status != null && Number(status) !== 1;
    },

    /**
     * All or nothing: a payload without `rows` is not applied at all (and reported as a load
     * error) rather than half-applied — otherwise the previous list would stay on screen next to
     * a "0 branches / not migrated" summary with the form disabled.
     *
     * @returns {boolean} whether the payload was applied
     */
    applyResponse(data) {
      if (!data || !Array.isArray(data.rows)) {
        this.markLoadFailed('ໂຫຼດລາຍການສາຂາບໍ່ໄດ້ — ຂໍ້ມູນທີ່ໄດ້ຮັບບໍ່ຖືກຕ້ອງ');
        return false;
      }

      this.rows = data.rows;
      this.isPersisted = data.is_persisted === true;
      /// `is_fallback` is the newer, complete flag; `is_readable` is kept for an older backend.
      this.isFallback = data.is_fallback === true || data.is_readable === false;
      this.today = data.today || '';
      this.loadError = false;
      this.loadErrorText = '';

      /// A fresh payload may have moved the row being edited into the expired group (the
      /// server's `today` advanced) or dropped it (another admin removed it). An editor left
      /// open on a row that changed under it would submit against stale state, so it is closed.
      if (this.rowMode.branch_id !== null) {
        const current = this.rows.find(row => Number(row.branch_id) === Number(this.rowMode.branch_id));
        if (!current || current.is_expired !== this.rowMode.wasExpired) {
          this.clearRowMode();
        }
      }

      return true;
    },

    /**
     * The list could not be loaded: nothing on screen may be trusted, so the tables are emptied
     * AND the form is locked (`canWrite` reads loadError) until a reload succeeds — a POST
     * against an unknown list state is worse than a disabled button.
     */
    markLoadFailed(text) {
      this.loadError = true;
      this.loadErrorText = text;
      this.rows = [];
      this.today = '';
      this.clearRowMode();
    },

    fetchData() {
      this.isLoading = true;
      this.cacheStale = false;
      this.$axios.get('auto-sign-excluded-branches').then(res => {
        this.isLoading = false;
        if (!this.applyResponse(res.data && res.data.data)) {
          this.notify('danger', this.loadErrorText);
        }
      }).catch(error => {
        this.isLoading = false;
        /// Same sentence in the banner and the toast — a 403 reads as "no permission" (the
        /// sidebar hides this page from non-SuperAdmins but the URL is typeable), a 404 as
        /// "backend not deployed yet", a network error as "no connection".
        const status = error.response && error.response.status;
        const text = status === 404
          ? 'ລະບົບຫຼັງບ້ານຍັງບໍ່ຮອງຮັບໜ້ານີ້ (ຍັງບໍ່ໄດ້ deploy) — ກະລຸນາແຈ້ງທີມ IT'
          : this.friendlyMessage(error);
        this.markLoadFailed(text);
        this.notify('danger', text);
      });
    },

    /**
     * Server-side search, debounced. The sequence number drops a slow earlier response that
     * would otherwise overwrite the results of the term the operator has since typed; a term
     * already fetched (e.g. re-opening the dropdown) is not fetched again.
     */
    searchBranches(term) {
      this.searchTerm = term || '';
      clearTimeout(this.searchTimer);

      if (this.searchTerm === this.lastFetchedTerm) {
        return;
      }

      this.searchTimer = setTimeout(() => {
        const seq = ++this.searchSeq;
        const fetchedTerm = this.searchTerm;
        this.searchingBranches = true;
        this.$axios.get('auto-sign-excluded-branches/branch-options', { params: { q: fetchedTerm, limit: 30 } })
          .then(res => {
            if (seq !== this.searchSeq) {
              return;
            }
            this.branchOptions = (res.data && res.data.data) || [];
            this.lastFetchedTerm = fetchedTerm;
            this.searchingBranches = false;
          })
          .catch(() => {
            if (seq !== this.searchSeq) {
              return;
            }
            this.branchOptions = [];
            this.searchingBranches = false;
          });
      }, 250);
    },

    resetForm() {
      this.form = { branch: null, excluded_until: '', reason: '' };
      this.addErrors = {};
    },

    add() {
      if (!this.canAdd) {
        return;
      }
      this.adding = true;
      this.addErrors = {};

      const payload = {
        branch_id: this.form.branch.id,
        excluded_until: this.form.excluded_until || null,
        reason: this.form.reason || null,
      };

      this.$axios.post('auto-sign-excluded-branches', payload).then(res => {
        this.adding = false;
        this.afterWrite(res, 'ເພີ່ມສາຂາສຳເລັດ');
        this.resetForm();
      }).catch(error => {
        this.adding = false;
        this.addErrors = this.errorsFrom(error, 'branch_id');
        this.notifyFailure(error);
      });
    },

    startEdit(row) {
      this.editErrors = {};
      this.rowMode = { branch_id: row.branch_id, mode: 'edit', wasExpired: row.is_expired === true };
      this.editing = {
        /// An expired row's date is in the past and the backend refuses past dates, so it must
        /// not be resubmitted by a reason-only edit. Start from "indefinitely"; the operator
        /// picks a new date if they want one.
        excluded_until: row.is_expired ? '' : (row.excluded_until || ''),
        reason: row.reason || '',
      };
    },

    startRemove(row) {
      this.editErrors = {};
      this.rowMode = { branch_id: row.branch_id, mode: 'remove', wasExpired: row.is_expired === true };
    },

    clearRowMode() {
      this.rowMode = { branch_id: null, mode: null };
      this.editing = { excluded_until: '', reason: '' };
      this.editErrors = {};
    },

    saveEdit(row) {
      this.savingEdit = true;
      this.editErrors = {};

      /// Both keys are always sent from the inline editor — it shows both, so the operator has
      /// seen (and kept or changed) both. A locked row's date is ignored server-side anyway.
      const payload = {
        excluded_until: this.editing.excluded_until || null,
        reason: this.editing.reason || null,
      };

      this.$axios.put(`auto-sign-excluded-branches/${row.branch_id}`, payload).then(res => {
        this.savingEdit = false;
        this.clearRowMode();
        this.afterWrite(res, 'ບັນທຶກສຳເລັດ');
      }).catch(error => {
        this.savingEdit = false;
        this.editErrors = this.errorsFrom(error, 'excluded_until');
        this.notifyFailure(error);
      });
    },

    remove(row) {
      this.removing = true;
      this.$axios.delete(`auto-sign-excluded-branches/${row.branch_id}`).then(res => {
        this.removing = false;
        this.clearRowMode();
        this.afterWrite(res, 'ລຶບສາຂາອອກຈາກລາຍການແລ້ວ');
      }).catch(error => {
        this.removing = false;
        this.clearRowMode();
        this.notifyFailure(error);
      });
    },

    afterWrite(res, fallbackMessage) {
      if (!this.applyResponse(res.data && res.data.data)) {
        /// The write went through but the refreshed list did not arrive in a usable shape —
        /// reload rather than leave the operator on an emptied screen.
        this.fetchData();
      }

      /// Saved, but the shared cache could not be invalidated — the sweep may keep the previous
      /// list for a few minutes. Saying only "ສຳເລັດ" would have the operator believe the change
      /// is already live.
      if (res.data && res.data.cache_stale) {
        this.cacheStale = true;
        this.$notification.openNotification(
          { title: 'ບັນທຶກແລ້ວ ແຕ່ຍັງບໍ່ທັນມີຜົນ', text: 'ຮອບກາງຄືນອາດຍັງໃຊ້ລາຍການເກົ່າ ປະມານ 5 ນາທີ' },
          'top-right', 'warn', 7000
        );
        return;
      }

      this.cacheStale = false;
      this.notify('dark', (res.data && res.data.message) || fallbackMessage, 3000);
    },

    /**
     * A failure arrives either as Laravel field errors (`errors.excluded_until[]`) or as our own
     * `{error, message}`. The latter is placed by its CODE — a date refusal belongs under the
     * date input whichever call raised it — and only otherwise under `fallbackField`. The text
     * is always {@link friendlyMessage}: a sentence the operator can act on, never a code.
     */
    errorsFrom(error, fallbackField) {
      const body = (error.response && error.response.data) || {};
      const errors = {};

      Object.keys(body.errors || {}).forEach(key => {
        errors[key] = [].concat(body.errors[key])[0];
      });

      if (!Object.keys(errors).length) {
        errors[this.fieldForErrorCode(body.error) || fallbackField] = this.friendlyMessage(error);
      }

      return errors;
    },

    fieldForErrorCode(code) {
      switch (code) {
        case 'INVALID_DATE':
        case 'DATE_IN_PAST':
          return 'excluded_until';
        case 'BRANCH_NOT_FOUND':
        case 'ALREADY_LISTED':
          return 'branch_id';
        default:
          return null;
      }
    },

    /**
     * One human sentence for any failed call, in this order of preference:
     *  1. the first field message (the backend's Lao validation text),
     *  2. the text for a backend error CODE (client-side copy, so a missing/English server
     *     message can never leak through),
     *  3. the backend's own `message` — unless it is one of Laravel's English defaults,
     *  4. a sentence for the HTTP status / no connection at all.
     */
    friendlyMessage(error) {
      const response = error && error.response;
      const body = (response && response.data) || {};
      const status = response && response.status;

      const fieldMessages = Object.keys(body.errors || {})
        .map(key => [].concat(body.errors[key])[0])
        .filter(text => typeof text === 'string' && text.trim() !== '');
      if (fieldMessages.length) {
        return fieldMessages[0];
      }

      const byCode = ERROR_CODE_TEXT[body.error];
      if (byCode) {
        return byCode;
      }

      if (typeof body.message === 'string' && body.message.trim() !== '' && !LARAVEL_DEFAULT_TEXT.test(body.message)) {
        return body.message;
      }

      if (!response) {
        return 'ເຊື່ອມຕໍ່ກັບລະບົບຫຼັງບ້ານບໍ່ໄດ້ — ກະລຸນາກວດອິນເຕີເນັດ ແລ້ວລອງໃໝ່';
      }

      return STATUS_TEXT[status] || 'ເກີດຂໍ້ຜິດພາດທີ່ບໍ່ຄາດຄິດ — ກະລຸນາລອງໃໝ່ ຫຼື ແຈ້ງທີມ IT';
    },

    notifyFailure(error) {
      this.notify('danger', this.friendlyMessage(error));
    },

    notify(color, text, duration = 5000) {
      this.$notification.openNotification({ title: 'ບໍ່ສຳເລັດບິນ Auto', text }, 'top-right', color, duration);
    },

    nextDay(date) {
      return date ? moment(date, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD') : '';
    },

    formatDate(date) {
      return date ? moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY') : '—';
    },

    formatDateTime(value) {
      return value ? moment(value).format('DD/MM/YYYY HH:mm') : '';
    },
  },
};
</script>

<style lang="scss" scoped>
/*
  The `.setting-*` values below deliberately MATCH ReturnRuleSettings.vue — the two panes sit
  in the same shell and must read as one page. Change them together.
*/
.auto-sign-excluded {
  padding: 4px 2px 24px;
}

.setting-hint {
  color: #6b7280;
  line-height: 1.7;
  margin: 8px 0 4px;
}

.setting-alert {
  margin-top: 12px;

  i {
    margin-right: 6px;
  }
}

.setting-alert-action {
  display: inline-block;
  margin-left: 8px;
}

.setting-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 16px 0 6px;
}

.setting-summary-item {
  flex: 1 1 180px;
  background: #f5f7fb;
  border-radius: 8px;
  padding: 10px 14px;
}

.setting-summary-label {
  display: block;
  font-size: 13px;
  color: #6b7280;
}

.setting-summary-value {
  display: block;
  font-size: 18px;
  font-weight: 600;
  color: #195BFF;
}

.setting-group {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px 16px 12px;
  margin-top: 16px;
}

.setting-group-head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 6px;

  h5 {
    font-size: 17px;
    font-weight: 600;
    margin: 0;
  }
}

.setting-field-note {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

.setting-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
}

.setting-footnote {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 14px;
  line-height: 1.7;
}

.text-danger {
  color: crimson;
  font-size: 13px;
  margin-left: 6px;
}

.text-warning {
  color: #b45309;
}

/* ---- pane-specific ---- */

.table-wrap {
  overflow-x: auto;
}

.excluded-table {
  font-size: 14px;

  th {
    white-space: nowrap;
    color: #374151;
  }

  td {
    vertical-align: middle;
  }

  .col-actions {
    white-space: nowrap;
    text-align: right;

    > * {
      display: inline-flex;
      vertical-align: middle;
    }
  }

  tr.is-locked td {
    background: #fffbeb;
  }

  tr.is-expired td {
    color: #6b7280;
    background: #fafafa;
  }

  tr.section-row td {
    background: #f3f4f6;
    color: #4b5563;
    font-size: 13px;
    font-weight: 600;

    i {
      margin-right: 6px;
    }
  }
}

.branch-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.branch-name {
  font-weight: 600;
  color: #111827;
}

.branch-meta {
  font-size: 12px;
  color: #6b7280;
}

.branch-locked {
  font-size: 12px;
  color: #b45309;
}

.confirm-text {
  font-size: 13px;
  color: #b91c1c;
  margin-right: 6px;
}

.branch-code {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  color: #374151;
}

.option-name {
  font-weight: 600;
}

.option-meta {
  margin-left: 6px;
  color: #6b7280;
  font-size: 12px;
}

.option-code {
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
}

.option-closed {
  color: crimson;
}

.option-listed {
  color: #195BFF;
}

/*
  vue-multiselect paints the hovered option blue (and hovered+selected red) with white text —
  our grey / red / blue meta colours vanish on it. Force white on those rows. ::v-deep because
  the option wrapper belongs to the multiselect component, not to this scoped template.
*/
::v-deep .multiselect__option--highlight {
  .option-name,
  .option-meta,
  .option-code,
  .option-closed,
  .option-listed {
    color: #ffffff;
  }

  .option-closed,
  .option-listed {
    font-weight: 600;
  }
}
</style>
